/**
 * SWT status consumers (Hub Tasks #11 and #12; Asana 1217186757080252).
 *
 * Two read-only Hub surfaces, proxied verbatim by the BFF:
 *  - enrollment-proof pipeline status ({ applicationId, assessment | null })
 *  - academic (professor/university) verification status
 *    ({ applicationId, vacationVerification | null, academicVerificationRequests })
 *
 * Applicant-safety contract: only ALLOWLISTED fields ever reach the render
 * path. OCR text, confidence values, storage URLs, provider errors, reason
 * codes and reviewer internals must never enter the browser copy — unknown
 * or unrecognized states collapse to generic applicant-safe phrases. The
 * portal never computes enrollment-proof or SWT eligibility (Rules Engine
 * owns it; the hard gate lives at Camp Contract).
 */
import { useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { portal } from './portal';

/* ------------------------------ Task #12 types ------------------------------ */

export interface SwtVacationVerification {
  id: string;
  verificationState: string; // UNVERIFIED | VERIFIED | PROOF_REQUESTED
  submittedStartDate: string | null;
  submittedEndDate: string | null;
  verifiedStartDate: string | null;
  verifiedEndDate: string | null;
  proofType: string | null; // SCHOOL_CALENDAR | PROFESSOR_VERIFICATION | null
}

export interface SwtAcademicVerificationRequest {
  id: string;
  contactName: string | null;
  contactRole: string | null;
  status: string; // PENDING | SUBMITTED | APPROVED | REJECTED
  confirmedEnrollmentTerm: string | null;
  confirmedVacationStart: string | null;
  confirmedVacationEnd: string | null;
}

export interface SwtAcademicVerificationStatus {
  vacationVerification: SwtVacationVerification | null;
  academicVerificationRequests: SwtAcademicVerificationRequest[];
}

/* --------------------- applicant-safe status projections --------------------- */

export interface SwtStatusBadge {
  /** Applicant-safe headline. */
  label: string;
  /** Visual tone for the badge/panel. */
  tone: 'pending' | 'success' | 'info';
}

/** Enrollment-proof assessment statuses the pipeline is still working on. */
const ASSESSMENT_ACTIVE = new Set(['pending', 'queued', 'processing', 'in_progress', 'extracting', 'assessing']);
const ASSESSMENT_VERIFIED = new Set(['completed', 'approved', 'verified', 'passed']);

/**
 * Project the Hub's enrollment-proof `assessment` into an applicant-safe
 * badge. Returns null when there is nothing to show (no assessment yet).
 * Anything unrecognized — including failure/manual-review states — collapses
 * to the generic "being reviewed" phrase: negative pipeline outcomes are a
 * staff matter and must not surface raw to the applicant.
 */
export function deriveEnrollmentProofBadge(assessment: unknown): SwtStatusBadge | null {
  if (assessment == null || typeof assessment !== 'object') return null;
  const status = String((assessment as Record<string, unknown>).status ?? '').toLowerCase();
  if (!status) return null;
  if (ASSESSMENT_ACTIVE.has(status)) {
    return { label: 'Your document is being processed — this usually takes a few minutes.', tone: 'pending' };
  }
  if (ASSESSMENT_VERIFIED.has(status)) {
    return { label: 'Your proof of student status has been received and checked.', tone: 'success' };
  }
  return { label: 'Your document is being reviewed by our team.', tone: 'info' };
}

/** True while the pipeline is actively working (drives the polling loop). */
export function isAssessmentActive(assessment: unknown): boolean {
  if (assessment == null || typeof assessment !== 'object') return false;
  const status = String((assessment as Record<string, unknown>).status ?? '').toLowerCase();
  return ASSESSMENT_ACTIVE.has(status);
}

/** Applicant-safe copy for the vacation-verification state. */
export function deriveVacationVerificationBadge(v: SwtVacationVerification | null | undefined): SwtStatusBadge | null {
  if (!v) return null;
  switch (v.verificationState) {
    case 'VERIFIED':
      return { label: 'Your vacation dates have been verified.', tone: 'success' };
    case 'PROOF_REQUESTED':
      return { label: 'Additional proof of your vacation dates has been requested.', tone: 'info' };
    default:
      return { label: 'Your vacation dates are awaiting verification.', tone: 'pending' };
  }
}

/** Applicant-safe copy per professor-verification request status. */
export function requestStatusLabel(status: string): string {
  switch (status) {
    case 'APPROVED':
      return 'Confirmed';
    case 'REJECTED':
      return 'Not confirmed — our team will follow up';
    case 'SUBMITTED':
      return 'Response received — under review';
    default:
      return 'Awaiting response';
  }
}

/* --------------------------------- hooks --------------------------------- */

const POST_SUBMIT_POLL_MS = 5 * 60 * 1000;
const POLL_INTERVAL_MS = 5000;

/**
 * Enrollment-proof pipeline status. Mirrors useCbcStatus: polls while the
 * assessment is active or during the 5-minute window after markSubmitted()
 * (the Hub creates the assessment asynchronously after upload/submit);
 * otherwise a single fetch. `enabled` gates the fetch to the step that
 * actually shows it. Errors are non-fatal: the step must stay usable when
 * the status surface is down (401/403/404/5xx render nothing).
 */
export function useSwtEnrollmentProofStatus(appId: string | number | undefined, enabled: boolean) {
  const submittedAtRef = useRef(0);
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['swtEnrollmentProof', String(appId)],
    queryFn: async () => (await portal.swtEnrollmentProofStatus(appId!)) as { assessment: unknown },
    enabled: enabled && appId != null && appId !== '',
    staleTime: 0,
    refetchInterval: (query) => {
      const active = isAssessmentActive(query.state.data?.assessment);
      const inWindow = Date.now() - submittedAtRef.current < POST_SUBMIT_POLL_MS;
      return active || inWindow ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
    retry: false,
  });
  const markSubmitted = useCallback(() => {
    submittedAtRef.current = Date.now();
    void queryClient.invalidateQueries({ queryKey: ['swtEnrollmentProof', String(appId)] });
  }, [appId, queryClient]);
  return useMemo(
    () => ({ assessment: q.data?.assessment ?? null, markSubmitted }),
    [q.data, markSubmitted],
  );
}

/**
 * Academic (professor/university) verification status. Read-only and
 * staff-driven upstream, so no polling loop — a fetch per step visit is
 * enough. Same non-fatal error posture as the enrollment-proof hook.
 */
export function useSwtAcademicVerificationStatus(appId: string | number | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: ['swtAcademicVerification', String(appId)],
    queryFn: async () => (await portal.swtAcademicVerificationStatus(appId!)) as SwtAcademicVerificationStatus,
    enabled: enabled && appId != null && appId !== '',
    staleTime: 60_000,
    retry: false,
  });
  return useMemo<SwtAcademicVerificationStatus>(
    () => ({
      vacationVerification: (q.data?.vacationVerification ?? null) as SwtVacationVerification | null,
      academicVerificationRequests: (q.data?.academicVerificationRequests ?? []) as SwtAcademicVerificationRequest[],
    }),
    [q.data],
  );
}

/** The SWT status panel is shown on the Proof of Student Status step only. */
export function isProofOfStudentStatusStep(stepKey: string | undefined): boolean {
  return !!stepKey && stepKey.startsWith('proof_of_student_status');
}
